/**
 * L'état des paiements, tous centres confondus.
 *
 * ## Pourquoi ces blocs ont quitté la liste des familles
 *
 * Demandé le 20 septembre 2026 : « La première partie de la page Situation
 * financière des familles (avec les états des paiements globaux) ne devrait pas
 * être dans cette page mais dans la partie Tableau financier. »
 *
 * C'est juste : un total de 61 800,54 € n'appartient pas à un écran dont le
 * sujet est *une* famille à la fois. Les blocs sont donc ici, montés par le
 * Tableau financier.
 *
 * ## Sauf le bandeau, qui reste aux deux endroits
 *
 * `BandeauAncienCircuit` n'est pas un total : c'est l'avertissement qui dit
 * pourquoi le reste dû de 399 familles est **surévalué**. Chaque ligne de la
 * liste porte sa propre mention « payé selon l'ancien circuit », et le bandeau
 * est ce qui l'explique. Le retirer de la liste laisserait 329 207,05 €
 * répartis en lignes qui se liraient comme des arriérés réels.
 *
 * Il est donc aux deux endroits, et c'est sans danger : ce n'est pas un
 * chiffre calculé deux fois, c'est la même phrase lue par la même vue.
 */

import type { ReactNode } from 'react';
import { AlertTriangle, Clock, Info, Wallet } from 'lucide-react';
import clsx from 'clsx';
import { euros, retard, TRANCHES } from '../../../lib/finances/situation';
import type { FinancialTotals } from '../../../lib/finances/gateway.supabase';

/**
 * L'avertissement, avant les chiffres qu'il qualifie.
 *
 * `onFiltrer` n'est fourni que là où une liste peut se filtrer — sur le
 * Tableau financier, il n'y a rien à filtrer, et un bouton qui ne mènerait
 * nulle part vaut moins que pas de bouton.
 */
export function BandeauAncienCircuit({
  totaux, onFiltrer,
}: {
  totaux: FinancialTotals | null;
  onFiltrer?: () => void;
}) {
  if (!totaux || totaux.legacyGapFamilyCount === 0) return null;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
      <p className="font-medium text-amber-900 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 shrink-0" />
        Les paiements de l'ancien circuit ne sont pas encore repris
      </p>
      <p className="text-sm text-amber-900 mt-2">
        Le reste dû se calcule sur les paiements rapprochés dans le nouveau
        modèle. <strong>{totaux.legacyGapFamilyCount} familles</strong> ont des factures que
        l'ancien circuit sait payées — {euros(totaux.legacyPaidCents)} au total,
        sur {totaux.legacyPaidInvoiceCount} factures — sans que le nouveau modèle le sache
        encore. Leur reste dû est donc surévalué, et un rappel envoyé à ces familles
        réclamerait de l'argent déjà reçu.
      </p>
      <p className="text-sm text-amber-900 mt-2">
        Les lignes concernées portent la mention <em>« payé selon l'ancien circuit »</em>.
      </p>
      {onFiltrer && (
        <button
          onClick={onFiltrer}
          className="mt-3 text-sm font-medium text-amber-900 underline decoration-dotted hover:decoration-solid"
        >
          N'afficher que ces {totaux.legacyGapFamilyCount} familles
        </button>
      )}
    </div>
  );
}

/** Les quatre chiffres, sommés EN BASE par une vue d'une ligne. */
export function TuilesFinancieres({ totaux }: { totaux: FinancialTotals | null }) {
  return (
    <>
    {/*
      Ce que les totaux ont écarté. Un total qui retranche sans le dire est
      aussi trompeur que celui qui comptait : mesuré le 20 septembre 2026,
      42 020,00 € de factures de test rendaient le total des notes de crédit
      faux à 43 %. Elles restent visibles partout ailleurs — elles servent
      encore à éprouver le logiciel.
    */}
    {totaux && totaux.horsComptabiliteCount > 0 && (
      <p className="mb-3 text-xs text-gray-500">
        Hors comptabilité : {euros(totaux.horsComptabiliteCents)} sur{' '}
        {totaux.horsComptabiliteCount} facture{totaux.horsComptabiliteCount > 1 ? 's' : ''}
        {' '}de test, écartées de ces totaux et visibles ailleurs.
      </p>
    )}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Tuile
        titre="Reste dû"
        valeur={totaux ? euros(totaux.outstandingCents) : '—'}
        detail={totaux ? `${totaux.overdueFamilyCount} familles en retard` : ''}
        ton="alerte"
        icone={<Clock className="h-4 w-4" />}
      />
      <Tuile
        titre="Facturé net"
        valeur={totaux ? euros(totaux.netCents) : '—'}
        detail={totaux ? `${euros(totaux.invoicedCents)} brut moins ${euros(totaux.creditNotesCents)} de notes de crédit` : ''}
        ton="neutre"
      />
      <Tuile
        titre="Reçu, nouveau modèle"
        valeur={totaux ? euros(totaux.receivedCents) : '—'}
        detail={totaux ? `${euros(totaux.legacyPaidCents)} de plus selon l'ancien circuit` : ''}
        ton="attention"
      />
      <Tuile
        titre="Provisions"
        valeur={totaux ? euros(totaux.provisionCents) : '—'}
        detail={totaux ? `${totaux.provisionFamilyCount} familles au crédit` : ''}
        ton="positif"
        icone={<Wallet className="h-4 w-4" />}
      />
    </div>
    </>
  );
}

/** L'ancienneté du reste dû, qui dit autre chose qu'un total. */
export function AncienneteDuResteDu({ totaux }: { totaux: FinancialTotals | null }) {
  if (!totaux || totaux.outstandingCents <= 0) return null;

  /*
    Les clés sont celles de `TRANCHES` — `due_soon_cents`, pas `dueSoon`. Les
    renommer en camelCase en extrayant ce bloc a rendu toutes les barres à
    zéro, sans aucune erreur : le graphe s'affichait, vide, et se lisait comme
    « aucun arriéré ».
  */
  const valeurs: Record<string, number> = {
    due_soon_cents: totaux.dueSoonCents,
    overdue_30_cents: totaux.overdue30Cents,
    overdue_90_cents: totaux.overdue90Cents,
    overdue_365_cents: totaux.overdue365Cents,
    overdue_beyond_cents: totaux.overdueBeyondCents,
  };
  const max = Math.max(1, ...Object.values(valeurs));
  const tranches = TRANCHES.map((t) => ({
    label: t.label,
    cents: valeurs[t.cle] ?? 0,
    part: (valeurs[t.cle] ?? 0) / max,
  }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <h2 className="font-medium text-gray-800 mb-1">Ancienneté du reste dû</h2>
      <p className="text-sm text-gray-600 mb-4">
        La plus ancienne échéance de chaque famille. Le retard le plus long est
        de {retard(totaux.maxDaysOverdue)}.
      </p>
      <div className="space-y-2">
        {tranches.map((t) => (
          <div key={t.label} className="flex items-center gap-3 text-sm">
            <span className="w-32 shrink-0 text-gray-600">{t.label}</span>
            <span className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
              <span
                className="block h-full bg-indigo-500"
                style={{ width: `${Math.max(t.part * 100, t.cents > 0 ? 1.5 : 0)}%` }}
              />
            </span>
            <span className="w-28 text-right font-medium tabular-nums">{euros(t.cents)}</span>
          </div>
        ))}
      </div>
      {totaux.cancelledOutstandingCents > 0 && (
        <p className="text-xs text-gray-500 mt-4 flex gap-2">
          <Info className="h-4 w-4 shrink-0" />
          <span>
            {euros(totaux.cancelledOutstandingCents)} portés par des factures annulées ne sont
            pas comptés ci-dessus : une facture annulée ne réclame plus rien.{' '}
            <strong>Elle reste pourtant une dette</strong> tant qu'une note de crédit ou une
            mise en perte ne l'a pas éteinte — c'est le premier motif d'écart du contrôle du
            compte client, plus bas.
          </span>
        </p>
      )}
    </div>
  );
}

function Tuile({
  titre, valeur, detail, ton, icone,
}: {
  titre: string;
  valeur: string;
  detail: string;
  ton: 'alerte' | 'attention' | 'positif' | 'neutre';
  icone?: ReactNode;
}) {
  const bordures: Record<string, string> = {
    alerte: 'border-l-red-500',
    attention: 'border-l-amber-500',
    positif: 'border-l-green-600',
    neutre: 'border-l-gray-300',
  };
  return (
    <div className={clsx('bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 p-4', bordures[ton])}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
        {icone}
        {titre}
      </p>
      <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{valeur}</p>
      {detail && <p className="text-xs text-gray-500 mt-1">{detail}</p>}
    </div>
  );
}
